import { useState } from 'react'
import classes from './ChatWidget.module.scss'
import { ChatOrder, ChatOrderCargoLine } from './types'
import { IconDownload, IconEye } from './icons'
import { GetEInvoiceAddressApi } from '../../pages/MySpecial/MyOrders/services/getEInvoiceAddressApi'
import { downloadFile, downloadFileFromUrl, formatPrice } from '../../base/utils/dataHelper'
import OrderDetailsPanel from './OrderDetailsPanel'
import YurticiTrackingButton from './YurticiTrackingButton'

const CURRENCIES = ['TRY', 'USD', 'EUR'] as const

function safeCurrency(value: string): 'TRY' | 'USD' | 'EUR' {
  return (CURRENCIES as readonly string[]).includes(value) ? (value as 'TRY' | 'USD' | 'EUR') : 'TRY'
}

function formatOrderDate(raw: string) {
  if (!raw) return raw
  // Backend tarihi "GG.AA.YYYY [HH:mm:ss]" (SAP) formatında gönderir; new Date() bunu AA.GG sanıp
  // gün/ayı ters çevirir. Bu yüzden noktalı/slash'lı GG.AA.YYYY'yi önce elle ayrıştırıyoruz.
  const trMatch = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/)
  if (trMatch) {
    const [, dd, mm, yyyy] = trMatch
    return `${dd.padStart(2, '0')}.${mm.padStart(2, '0')}.${yyyy}`
  }
  const d = new Date(raw)
  if (isNaN(d.getTime())) return raw
  return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
}

/** Faz-44: "Kargo: Teslim edildi · 17.10.2026" — metin ve tarih sunucu şablonudur, burada yalnız birleşir. */
function cargoLineText(line: ChatOrderCargoLine) {
  return `Kargo: ${line.text}${line.date ? ` · ${line.date}` : ''}`
}

function isInvoiced(invoiceNumber?: string) {
  const v = (invoiceNumber ?? '').trim()
  return v !== '' && v !== '-'
}

interface Props {
  order: ChatOrder
  disabled?: boolean
}

/**
 * Tek sipariş kartı: rozet + "Detayları Gör" (gerçek OrdersHistory/GetOrderDetails çağrısı) +
 * "Faturayı İndir" (gerçek MyReports/GetEInvoiceAddress çağrısı). Veri kaynağından bağımsız —
 * son sipariş / son N sipariş / numaralı sipariş sorgularının hepsi aynı kartı kullanır.
 */
export default function OrderCard({ order, disabled }: Props) {
  const [detailExpanded, setDetailExpanded] = useState(false)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)

  const currency = safeCurrency(order.currency)
  const invoiced = isInvoiced(order.invoiceNumber)

  const toggleDetail = () => {
    if (disabled) return
    setDetailExpanded(currentValue => !currentValue)
  }

  const handleDownloadInvoice = async () => {
    if (disabled || invoiceLoading) return
    setInvoiceLoading(true)
    setInvoiceError(null)
    try {
      const res: string = await new GetEInvoiceAddressApi({} as any).getEInvoiceAddress({
        InvoiceNumber: order.eInvoiceNumber,
        DateTime: order.invoiceDate,
      })

      if (!res || res === 'Dosya bulunamadı.') {
        setInvoiceError('Fatura bulunamadı.')
        return
      }

      const fileName = `Fatura-${order.invoiceNumber || order.orderNumber}.pdf`

      if (res.startsWith('http://') || res.startsWith('https://')) {
        downloadFileFromUrl(fileName, res)
      } else {
        const base64 = res.replace(/^data:application\/pdf;base64,/, '')
        downloadFile(fileName, base64, 'application/pdf', true)
      }
    } catch (err: any) {
      setInvoiceError(err?.description || 'Fatura indirilemedi.')
    } finally {
      setInvoiceLoading(false)
    }
  }

  return (
    <div className={classes.orderCard}>
      <div className={classes.orderCardHead}>
        <div>
          <div className={classes.orderNumber}>Sipariş No: {order.orderNumber}</div>
          <div className={classes.orderDate}>{formatOrderDate(order.orderDate)}</div>
        </div>
        <div className={classes.orderAmount}>{formatPrice(order.amount, currency, 'symbol')}</div>
      </div>

      <div className={classes.orderCardBody}>
        <span className={`${classes.orderBadge} ${invoiced ? classes.orderBadgeInvoiced : classes.orderBadgePending}`}>
          {invoiced ? 'Faturalandı' : 'Faturalandırılmadı'}
        </span>

        <div className={classes.orderActions}>
          <button
            type='button'
            className={`${classes.orderActionBtn} ${classes.tipTop}`}
            data-tip='Detayları Gör'
            aria-label='Detayları Gör'
            aria-expanded={detailExpanded}
            onClick={toggleDetail}
            disabled={disabled}
          >
            <IconEye />
          </button>
          <button
            type='button'
            className={`${classes.orderActionBtn} ${classes.tipTop}`}
            data-tip='Faturayı İndir'
            aria-label='Faturayı İndir'
            onClick={handleDownloadInvoice}
            disabled={disabled || invoiceLoading || !order.eInvoiceNumber}
          >
            <IconDownload />
          </button>
        </div>
      </div>

      {/* Faz-44: teslimat başına sunucu şablonu kargo satırı + doğrulanmış Yurtiçi bağlantısı. */}
      {order.cargo && order.cargo.length > 0 && (
        <div className={classes.orderCargoLines}>
          {order.cargo.map((line, index) => (
            <div key={`${line.deliveryNumber ?? 'cargo'}-${index}`} className={classes.orderCargoLine}>
              <span className={classes.orderCargoText}>{cargoLineText(line)}</span>
              {line.trackingUrl && <YurticiTrackingButton url={line.trackingUrl} />}
            </div>
          ))}
        </div>
      )}

      {invoiceError && <div className={classes.orderDetailError}>{invoiceError}</div>}

      <OrderDetailsPanel order={order} open={detailExpanded} disabled={disabled} />
    </div>
  )
}
